// ---------------------------------------------------------------------------
// LeftPanel — Wraps LayerTree, SearchPanel, and InsertPanel with a tabbed
// interface. Replaces the old LeftPanelContent switch in EditorShell.
//
// Tab bar:
//   [Layers]  — shows LayerTree (or SearchPanel when in search mode)
//   [Insert]  — shows InsertPanel (element catalog + block creation)
// ---------------------------------------------------------------------------

import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { LayerTree } from './layer-tree'
import { SearchPanel } from './search-panel'
import { InsertPanel } from './insert-panel'
import { getLibraryPanelCopy } from './library-panel-copy'
import { useStudioLocale } from '@/i18n'
import { cn } from '@/lib/utils'

export interface LeftPanelProps {
  embedded?: boolean
}

export function LeftPanel({ embedded = false }: LeftPanelProps) {
  const activeRail = useEditorStore((s) => s.activeRail)
  const { locale } = useStudioLocale()
  const copy = getLibraryPanelCopy(locale)

  // Derive which tab is active: 'layers'/'search' -> Layers tab, 'insert' -> Insert tab
  const activeTab = activeRail === 'insert' ? 'insert' : 'layers'

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Tab bar */}
      {!embedded && (
        <div className="flex border-b border-foreground/10">
          <button
            onClick={() => useEditorStore.getState().setActiveRail('layers')}
            className={cn(
              'flex-1 py-2 text-[10px] uppercase tracking-widest font-semibold text-center transition-colors outline-none',
              activeTab === 'layers'
                ? 'text-foreground border-b-2 border-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {copy.layersTab}
          </button>
          <button
            onClick={() => useEditorStore.getState().setActiveRail('insert')}
            className={cn(
              'flex-1 py-2 text-[10px] uppercase tracking-widest font-semibold text-center transition-colors outline-none',
              activeTab === 'insert'
                ? 'text-foreground border-b-2 border-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {copy.insertTab}
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeRail === 'search' ? (
          <SearchPanel />
        ) : activeRail === 'insert' ? (
          <InsertPanel />
        ) : (
          <LayerTree />
        )}
      </div>
    </div>
  )
}
